import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  alpha,
  Box,
  Checkbox,
  Divider,
  Radio,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Icon from "../../components/Icon/Icon";
import { useFilterOptions } from "./useFilterOptions";

/** Cuenta cuántos valores de filtro hay activos, para el contador del toggle */
export const countActiveFilters = (advanced = {}) =>
  Object.values(advanced).reduce(
    (total, value) => total + (Array.isArray(value) ? value.length : 1),
    0
  );

/**
 * Toggle de las opciones. Va dentro del input, así que no lleva bordes ni
 * fondo propio: solo texto y una flecha que gira al abrir.
 */
export const SearchOptionsToggle = ({ open, onToggle, activeCount }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const isActive = activeCount > 0;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 4,
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
        transition: "background-color 0.2s ease-in-out",
        "&:hover, &:focus-visible": {
          bgcolor: alpha(theme.palette.blueGray100.main, 0.9),
        },
      }}
    >
      <Typography
        variant="body2"
        fontSize={13}
        fontWeight={isActive ? 600 : 400}
        color="inherit"
      >
        {isActive ? `${t("searchBar.options")} (${activeCount})` : t("searchBar.options")}
      </Typography>
      {/* El prop color de Icon solo pinta la raíz del svg; los paths se
          fuerzan por CSS para que la flecha siga el color del toggle. */}
      <Box
        sx={{
          display: "flex",
          "& svg": { fill: "currentColor" },
          "& svg path": { fill: "currentColor" },
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s ease-in-out",
        }}
      >
        <Icon name="outline_arrow_down" size={14} />
      </Box>
    </Stack>
  );
};

/** Una opción del panel: checkbox (multi) o radio (valor único) */
const OptionRow = ({ checked, multiple, label, iconName, iconColor, onClick, disabled }) => {
  const theme = useTheme();
  const Control = multiple ? Checkbox : Radio;

  return (
    <Stack
      direction="row"
      alignItems="center"
      onClick={disabled ? undefined : onClick}
      sx={{
        borderRadius: 1,
        py: 0.35,
        pr: 1,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        "&:hover": disabled
          ? {}
          : { bgcolor: alpha(theme.palette.blueGray100.main, 0.7) },
      }}
    >
      <Control size="small" checked={checked} disabled={disabled} sx={{ p: 0.5, mr: 0.5 }} />
      {iconName && (
        <Icon
          name={iconName}
          size={15}
          color={iconColor || theme.palette.blueGray500.main}
          style={{ marginRight: 6, flexShrink: 0 }}
        />
      )}
      <Typography variant="body2" fontSize={13.5} noWrap>
        {label}
      </Typography>
    </Stack>
  );
};

// El panel usa el alto disponible en vez de un tope fijo chico: hay espacio de
// sobra y con listas largas (indicadores, anios, catalogos) se agradece.
const PANEL_HEIGHT = "min(62vh, 560px)";

/** Entrada del riel izquierdo */
const GroupRow = ({ label, count, selected, onClick }) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.1,
        cursor: "pointer",
        borderLeft: `3px solid ${selected ? theme.palette.primary.main : "transparent"}`,
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.08)
          : "transparent",
        "&:hover": {
          bgcolor: selected
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.blueGray100.main, 0.7),
        },
      }}
    >
      <Typography
        variant="body2"
        fontSize={13.5}
        fontWeight={selected ? 600 : 400}
        color={selected ? "primary" : "text.primary"}
        noWrap
      >
        {label}
      </Typography>
      {count > 0 && (
        <Box
          sx={{
            minWidth: 18,
            px: 0.5,
            borderRadius: 3,
            textAlign: "center",
            fontSize: 10,
            fontWeight: 700,
            color: theme.palette.primary.contrastText,
            bgcolor: theme.palette.primary.main,
          }}
        >
          {count}
        </Box>
      )}
    </Stack>
  );
};

/**
 * Panel de opciones del dominio activo, en maestro/detalle: a la izquierda los
 * grupos con su conteo, a la derecha solo las opciones del grupo elegido.
 *
 * Antes se pintaban de golpe las ~45 opciones de todos los grupos. Mostrar uno
 * a la vez además hace que los catálogos del formulario escalen: son más
 * entradas del riel, no más columnas.
 *
 * Todo sale del registro de dominios, así que cambiar de Denuncias a
 * Investigaciones cambia el contenido sin tocar este archivo.
 */
export const SearchOptionsPanel = ({
  domain,
  fields,
  onToggleField,
  advanced,
  onToggleFilterValue,
  onSetFilterValue,
  onClearAll,
  isLoadingCatalogues = false,
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const optionsCatalog = useFilterOptions();

  // Un filtro de catálogo trae su nombre y sus opciones ya resueltos desde el
  // formulario; uno estático nombra la llave i18n y el catálogo a resolver.
  const resolveLabel = (filter) => filter.label || t(filter.labelKey);
  const resolveOptions = (filter) =>
    Array.isArray(filter.options)
      ? filter.options
      : optionsCatalog[filter.options] || [];

  const generalFilters = domain.filters.filter((filter) => !filter.isCatalogue);
  const catalogueFilters = domain.filters.filter((filter) => filter.isCatalogue);

  const FIELDS_GROUP = "__fields";
  const [selected, setSelected] = useState(FIELDS_GROUP);

  // Cambiar de dominio puede dejar seleccionado un grupo que ya no existe
  const exists =
    selected === FIELDS_GROUP ||
    domain.filters.some((filter) => filter.key === selected);
  const activeKey = exists ? selected : FIELDS_GROUP;
  const activeFilter = domain.filters.find((filter) => filter.key === activeKey);

  const railSx = {
    borderRight: { sm: `1px solid ${alpha(theme.palette.common.black, 0.08)}` },
    borderBottom: {
      xs: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
      sm: "none",
    },
    maxHeight: { sm: PANEL_HEIGHT },
    overflowY: "auto",
    py: 1,
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        borderRadius: 4,
        border: `1px solid ${theme.palette.blueGray200.main}`,
        boxShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.08)}`,
        bgcolor: theme.palette.background.paper,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
        }}
      >
        {/* Maestro: grupos */}
        <Stack sx={railSx}>
          <GroupRow
            label={t("searchBar.where_to_search_short")}
            // Con campo único el conteo siempre sería 1: no aporta nada
            count={domain.singleField ? 0 : fields.length}
            selected={activeKey === FIELDS_GROUP}
            onClick={() => setSelected(FIELDS_GROUP)}
          />

          {generalFilters.map((filter) => (
            <GroupRow
              key={filter.key}
              label={resolveLabel(filter)}
              count={(advanced[filter.key] || []).length}
              selected={activeKey === filter.key}
              onClick={() => setSelected(filter.key)}
            />
          ))}

          {(isLoadingCatalogues || catalogueFilters.length > 0) && (
            <Divider sx={{ my: 0.5 }}>
              <Typography variant="body2" fontSize={9} color="text.secondary">
                {t("searchBar.form_catalogues")}
              </Typography>
            </Divider>
          )}

          {isLoadingCatalogues
            ? Array.from({ length: 3 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="text"
                  animation="wave"
                  sx={{ mx: 1.5, my: 0.5, fontSize: 14 }}
                />
              ))
            : catalogueFilters.map((filter) => (
                <GroupRow
                  key={filter.key}
                  label={resolveLabel(filter)}
                  count={(advanced[filter.key] || []).length}
                  selected={activeKey === filter.key}
                  onClick={() => setSelected(filter.key)}
                />
              ))}
        </Stack>

        {/* Detalle: opciones del grupo elegido */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
            alignContent: "start",
            rowGap: 0.5,
            columnGap: 3,
            p: 2.5,
            maxHeight: { sm: PANEL_HEIGHT },
            overflowY: "auto",
          }}
        >
          {activeKey === FIELDS_GROUP
            ? domain.fields.map((field) => {
                const checked = fields.includes(field.key);
                // Con campo único, tocar el activo no hace nada: el radio ya lo
                // comunica. Con varios, no se puede quitar el último.
                const isLocked =
                  checked && (domain.singleField || fields.length === 1);

                const hint = field.enabled
                  ? isLocked && !domain.singleField
                    ? t("searchBar.at_least_one_field")
                    : field.hintKey && t(field.hintKey)
                  : t(field.disabledReasonKey);

                return (
                  <Tooltip
                    key={field.key}
                    title={hint || ""}
                    placement="top-start"
                    disableInteractive
                  >
                    <span>
                      <OptionRow
                        multiple={!domain.singleField}
                        checked={checked}
                        disabled={!field.enabled}
                        label={t(field.labelKey)}
                        iconName={!field.enabled ? "outline_lock" : undefined}
                        onClick={() => !isLocked && onToggleField(field.key)}
                      />
                    </span>
                  </Tooltip>
                );
              })
            : resolveOptions(activeFilter).map((option) => {
                const selectedValue = advanced[activeFilter.key];
                const checked = activeFilter.multiple
                  ? (selectedValue || []).includes(option.value)
                  : selectedValue === option.value;

                return (
                  <OptionRow
                    key={option.value}
                    multiple={activeFilter.multiple}
                    checked={checked}
                    label={option.label}
                    iconName={option.iconName}
                    iconColor={option.color}
                    onClick={() =>
                      activeFilter.multiple
                        ? onToggleFilterValue(activeFilter.key, option.value)
                        : onSetFilterValue(
                            activeFilter.key,
                            checked ? "" : option.value
                          )
                    }
                  />
                );
              })}
        </Box>
      </Box>

      {countActiveFilters(advanced) > 0 && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{
            px: 2.5,
            py: 1,
            borderTop: `1px solid ${alpha(theme.palette.common.black, 0.07)}`,
            bgcolor: alpha(theme.palette.blueGray100.main, 0.4),
          }}
        >
          <Typography
            variant="body2"
            color="error"
            fontSize={12}
            sx={{ cursor: "pointer" }}
            onClick={onClearAll}
          >
            {t("searchBar.clear_all_filters")}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
